import React, { useContext, useState, useCallback } from 'react';

import { noop } from 'ui-components/default-props/noop';

import { emptyObject } from 'ui-components/default-props/empty';
import { usePaymentSurchargePreview } from '../../../../financial-services-ui/surcharge/hooks';
import {
  ALL_FUND_AMTS,
  DEFAULT_CUTOFF,
  DEFAULT_AUTO_FUND_AMOUNT,
  DEFAULT_PURCHASE_AMOUNT,
  DEFAULT_THRESHOLD,
  MINIMUM_PURCHASE_AMT,
} from '../components/constants/add-funds-constants';
import useAddFunds from '../hooks/use-add-funds';
import useAutoFunds from '../hooks/use-auto-funds';

const AddFundsContext = React.createContext({});
export const useAddFundsContext = () => useContext(AddFundsContext);

/**
 * @typedef {import('../../../../prop-types/currency-types').Currency} Currency
 * @typedef {{threshold: Currency | null, amount: Currency | null, cutoff: number | null}} AutoFundConfig
 */

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {{threshold?: Currency, amount?: Currency, cutoff?: number}} [props.autoFundConfig]
 * @param {{amount?: Currency, code?: string}} [props.defaultPurchaseConfig]
 * @param {(amount: Currency) => void | Promise<void>} [props.onAddFunds]
 * @param {(amount: Currency) => void | Promise<void>} [props.onSaveDefaultFundingAmount]
 * @param {(config: AutoFundConfig) => void | Promise<void>} [props.onSaveAutoFunding]
 * @param {(result: Currency | AutoFundConfig) => void} props.onSuccess
 * @param {boolean} props.submitting
 * @param {(submitting: boolean) => void} props.setSubmitting
 * @param {string} props.currencyCode
 * @param {{code?: string, value?: number}} [props.orderRates]
 * @param {string} [props.trackingId]
 * @returns {JSX.Element}
 */
export const AddFundsContextProvider = ({
  children,
  autoFundConfig = emptyObject,
  defaultPurchaseConfig = emptyObject,
  onAddFunds = noop,
  onSaveDefaultFundingAmount = noop,
  onSaveAutoFunding = noop,
  onSuccess,
  submitting,
  setSubmitting,
  currencyCode,
  orderRates = emptyObject,
  trackingId,
}) => {
  const isAutoFundEnabled = !!autoFundConfig?.threshold?.value;
  const initialFundAmount = defaultPurchaseConfig?.amount?.value;

  const [autoFundEnabled, setAutoFundEnabled] = useState(autoFundConfig?.threshold !== undefined ? isAutoFundEnabled : true);
  const [balanceThreshold, setBalanceThreshold] = useState(autoFundConfig?.threshold?.value ?? DEFAULT_THRESHOLD);
  const [autoFundAmount, setAutoFundAmount] = useState(autoFundConfig?.amount?.value ?? DEFAULT_AUTO_FUND_AMOUNT);
  const [maxDailyFundings, setMaxDailyFundings] = useState(autoFundConfig?.cutoff ?? DEFAULT_CUTOFF);
  const orderRateValue = orderRates?.value >= DEFAULT_PURCHASE_AMOUNT ? orderRates.value : DEFAULT_PURCHASE_AMOUNT;
  // When orderRates is provided (label creation flow), match AddFundButtons logic:
  // Buttons use calculateStartingButtonIndex which finds first value >= minimumPurchaseAmount + 1
  const minimumForButtonCalc = Math.max(orderRates?.value ?? 0, MINIMUM_PURCHASE_AMT);
  const initialButtonValue = orderRates?.value
    ? (ALL_FUND_AMTS.find((val) => val >= minimumForButtonCalc + 1) ?? ALL_FUND_AMTS[ALL_FUND_AMTS.length - 1])
    : orderRateValue;
  const [fundAmount, setFundAmount] = useState(initialFundAmount ?? initialButtonValue);
  const [useAsDefault, setUseAsDefault] = useState(false);

  const { updateAutoFundsConfig, errors: autoFundErrors } = useAutoFunds({
    currencyCode,
    onSuccess,
    onSaveAutoFunding,
  });
  const { purchaseFunds, errors: addFundErrors } = useAddFunds({
    currencyCode,
    onSuccess,
    onAddFunds,
    onSaveDefaultFundingAmount,
    trackingId,
  });
  const {
    loading: isSurchargeLoading,
    creditCardProcessingFee,
    creditCardProcessingFeePercentage,
    totalWithFees,
    error: previewError,
    triggerRetry,
  } = usePaymentSurchargePreview({ total: fundAmount });

  const updateAutoFundEnabled = useCallback(() => {
    if (!autoFundEnabled) {
      setAutoFundAmount(DEFAULT_AUTO_FUND_AMOUNT);
    }
    setAutoFundEnabled(!autoFundEnabled);
  }, [autoFundEnabled]);

  /** @type {import('./add-funds-context-types').AddFundsContextValue} */
  const value = {
    submitting,
    setSubmitting,
    autoFundEnabled,
    setAutoFundEnabled,
    balanceThreshold,
    setBalanceThreshold,
    autoFundAmount,
    setAutoFundAmount,
    maxDailyFundings,
    setMaxDailyFundings,
    fundAmount,
    setFundAmount,
    useAsDefault,
    setUseAsDefault,
    updateAutoFundsConfig,
    autoFundErrors,
    purchaseFunds,
    addFundErrors,
    autoFundConfig,
    defaultPurchaseConfig,
    orderRates,
    updateAutoFundEnabled,
    creditCardProcessingFee,
    creditCardProcessingFeePercentage,
    totalWithFees,
    isSurchargeLoading,
    previewError,
    triggerRetry,
  };

  return <AddFundsContext.Provider value={value}>{children}</AddFundsContext.Provider>;
};
